import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  RefreshCw,
  Plus,
  Search,
  Clock,
  User,
  MapPin,
  Grid,
  HelpCircle,
  Eye,
  X,
  Check,
  XCircle,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Calculator,
} from "lucide-react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios from "axios";
import "./CommandsNew.css";
import { base_api_url } from "../../../environment";
import { all_routes } from "../../../Router/all_routes";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "feather-icons-react/build/IconComponents";
import SessionControl from "../../components/SessionControl/sessioncontrol";
import AbonnementControl from "../../components/SessionControl/AbonnementControl";

const CommandsNew = () => {
  const MySwal = withReactContent(Swal);
  const restaurantName =
    JSON.parse(localStorage.getItem("restaurant") || "{}")?.restaurant?.nom ||
    "votre établissement";

  // États principaux
  const [activeTab, setActiveTab] = useState("Toutes");
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("Tous"); // Nouveau filtre paiement
  const [commands, setCommands] = useState([]);
  const [filteredCommands, setFilteredCommands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCommand, setSelectedCommand] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [relatedCommandsData, setRelatedCommandsData] = useState(null);
  const [showRelatedCommands, setShowRelatedCommands] = useState(false);
  const [processingGroupPayment, setProcessingGroupPayment] = useState(false);

  // 🆕 États pour la nouvelle interface de paiement groupé
  const [selectedCommandsIds, setSelectedCommandsIds] = useState([]);
  const [expandedCommands, setExpandedCommands] = useState([]);

  const navigate = useNavigate();
  const route = all_routes;

  // Statut mapping
  const statusMap = useMemo(
    () => ({
      10: "À valider",
      2: "En préparation",
      3: "En préparation",
      4: "À servir",
      5: "Terminée",
      0: "Annulée",
    }),
    [],
  );

  // Couleurs des badges de statut
  const statusColors = useMemo(
    () => ({
      "À valider": { bg: "#FEE2E2", text: "#DC2626", border: "#FECACA" },
      "En préparation": { bg: "#FED7AA", text: "#EA580C", border: "#FDBA74" },
      "À servir": { bg: "#D1FAE5", text: "#059669", border: "#A7F3D0" },
      "Livré": { bg: "#DBEAFE", text: "#2563EB", border: "#BFDBFE" },
      Terminée: { bg: "#C7D2DD", text: "#64748B", border: "#B0BCC9" },
      Annulée: { bg: "#E5E7EB", text: "#6B7280", border: "#D1D5DB" },
    }),
    [],
  );

  // Fonction pour obtenir le type de table
  const getTableType = (typeTable) => {
    if (typeTable === null || typeTable === 1) return "Classique";
    if (typeTable === 2) return "Livraison";
    if (typeTable === 3) return "QR Code Client";
    return null;
  };

  // Fonction pour obtenir le bon statut en tenant compte du statut_paiement
  const getStatusLabel = (statut, statutPaiement) => {
    if (statut === 10) return "À valider";
    if (statut === 2 || statut === 3) return "En préparation";
    if (statut === 4) return "À servir";
    if (statut === 5 && statutPaiement === 1) return "Livré";
    if (statut === 5 && statutPaiement === 2) return "Terminée";
    if (statut === 0) return "Annulée";
    return statusMap[statut] || "Inconnu";
  };

  // Gestion des erreurs avec toast
  const showErrorToast = (message) => {
    MySwal.fire({
      icon: "error",
      text: message,
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 4000,
      customClass: { popup: "pixel-toast" },
    });
  };

  const showSuccessToast = (message) => {
    MySwal.fire({
      icon: "success",
      text: message,
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 3000,
      customClass: { popup: "pixel-toast" },
    });
  };

  // Fetch des commandes avec gestion d'erreur améliorée
  const fetchCommands = async () => {
    setLoading(true);
    setError(null);

    try {
      const userStr = localStorage.getItem("user");
      if (!userStr) {
        throw new Error("Session expirée. Veuillez vous reconnecter.");
      }

      const user = JSON.parse(userStr);
      const { access_token, restoId } = user;

      if (!access_token) {
        throw new Error("Token d'authentification manquant.");
      }

      if (!restoId) {
        setLoading(false);
        return;
      }

      const url = `${base_api_url}api/commande/restaurant/${restoId}/dernieres-24h`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${access_token}`,
          "Content-Type": "application/json",
        },
        timeout: 10000,
      });

      if (!response.data) {
        throw new Error("Réponse invalide du serveur.");
      }

      const commandsData = response.data.commandes || [];

      if (!Array.isArray(commandsData)) {
        throw new Error("Format de données invalide.");
      }
      console.log(commandsData)
      setCommands(commandsData);
      setError(null);
    } catch (error) {
      console.error("Erreur lors du chargement des commandes:", error);

      let errorMessage = "Impossible de charger les commandes";

      if (error.message.includes("Session expirée")) {
        errorMessage = error.message;
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      } else if (error.code === "ECONNABORTED") {
        errorMessage = "Délai d'attente dépassé. Vérifiez votre connexion.";
      } else if (error.response) {
        if (error.response.status === 401) {
          errorMessage = "Session expirée. Reconnexion nécessaire.";
          setTimeout(() => {
            window.location.href = "/login";
          }, 2000);
        } else if (error.response.status === 403) {
          errorMessage = "Accès non autorisé.";
        } else if (error.response.status === 404) {
          errorMessage = "Restaurant non trouvé.";
        } else if (error.response.status >= 500) {
          errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
        } else {
          errorMessage = error.response.data?.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage =
          "Impossible de contacter le serveur. Vérifiez votre connexion.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      showErrorToast(errorMessage);
      setCommands([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommands();
  }, [refreshing]);

  // Filtrage dynamique avec gestion d'erreur
  useEffect(() => {
    try {
      let filtered = [...commands];

      if (activeTab !== "Toutes") {
        filtered = filtered.filter((cmd) => {
          if (!cmd || typeof cmd.statut === "undefined") return false;

          if (activeTab === "À valider") {
            return cmd.statut === 10;
          }

          if (activeTab === "En préparation") {
            return cmd.statut === 2 || cmd.statut === 3;
          }

          if (activeTab === "À servir") {
            return cmd.statut === 4;
          }

          if (activeTab === "Livré") {
            return cmd.statut === 5 && cmd.statut_paiement === 1;
          }

          if (activeTab === "Terminée") {
            return cmd.statut === 5 && cmd.statut_paiement === 2;
          }

          if (activeTab === "Annulée") {
            return cmd.statut === 0;
          }

          return (
            statusMap[cmd.statut] === activeTab || cmd.statut === activeTab
          );
        });
      }

      // Filtre par statut de paiement
      if (paymentStatusFilter !== "Tous") {
        filtered = filtered.filter((cmd) => {
          if (paymentStatusFilter === "À encaisser") {
            return cmd.statut_paiement === 1 || cmd.statut_paiement === '1';
          }
          if (paymentStatusFilter === "Payé") {
            return cmd.statut_paiement === 2 || cmd.statut_paiement === '2';
          }
          return true;
        });
      }

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = filtered.filter((cmd) => {
          if (!cmd) return false;

          const id =
            cmd.reference?.toLowerCase() ||
            cmd.id_commande?.toString().toLowerCase() ||
            "";
          const server = cmd.nom?.toLowerCase() || "";
          const table =
            cmd.numtable?.toString().toLowerCase() ||
            cmd.table_restaurant?.numero_table?.toString().toLowerCase() ||
            "";

          return (
            id.includes(searchLower) ||
            server.includes(searchLower) ||
            table.includes(searchLower)
          );
        });
      }

      setFilteredCommands(filtered);
    } catch (error) {
      console.error("Erreur lors du filtrage:", error);
      setFilteredCommands([]);
    }
  }, [commands, activeTab, searchTerm, paymentStatusFilter, statusMap]);

  // Refresh manuel
  const handleRefresh = () => {
    setRefreshing((r) => !r);
  };

  // Handlers pour le modal
  const handleOpenModal = async (cmd) => {
    setSelectedCommand(cmd);
    setIsModalOpen(true);
    setShowRelatedCommands(false); // Reset to show main command
    setRelatedCommandsData(null); // Clear previous data

    // Si la commande concerne une table type 3, fetch infos supplémentaires
    if (cmd.table_restaurant && cmd.table_restaurant.type_table === 3) {
      try {
        const token = JSON.parse(localStorage.getItem("user"))?.access_token;
        const numero_table = cmd.table_restaurant.numero_table;
        const user = JSON.parse(localStorage.getItem("user"));
        if (numero_table) {
          const url = `${base_api_url}api/commande_table/${numero_table}/${user.restoId}`;
          const response = await fetch(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });
          const data = await response.json();
          console.log("Commande table (type 3):", data);

          // Store related commands data if available
          if (
            data &&
            Array.isArray(data.commandes) &&
            data.commandes.length > 0
          ) {
            setRelatedCommandsData(data);
          }
        }
      } catch (err) {
        console.error("Erreur fetch commande_table:", err);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setShowRelatedCommands(false);
    setRelatedCommandsData(null);
    setTimeout(() => setSelectedCommand(null), 300);
  };

  const handleShowRelatedCommands = () => {
    setShowRelatedCommands(true);
    // Initialiser tous les états pour la nouvelle interface
    if (relatedCommandsData?.commandes) {
      // Expand toutes les commandes par défaut
      setExpandedCommands(relatedCommandsData.commandes.map(cmd => cmd.id_commande));
      // Réinitialiser la sélection
      setSelectedCommandsIds([]);
    }
  };

  const handleBackToMainCommand = () => {
    setShowRelatedCommands(false);
    // Réinitialiser les sélections
    setSelectedCommandsIds([]);
    setExpandedCommands([]);
  };

  // 🆕 Fonctions pour la gestion du paiement groupé moderne

  // Toggle expansion d'une commande
  const toggleCommandExpansion = useCallback((commandId) => {
    setExpandedCommands(prev =>
      prev.includes(commandId)
        ? prev.filter(id => id !== commandId)
        : [...prev, commandId]
    );
  }, []);

  // Toggle sélection d'une commande
  const toggleCommandSelection = useCallback((commandId, isPaid) => {
    if (isPaid) return; // Ne pas permettre la sélection des commandes payées

    setSelectedCommandsIds(prev =>
      prev.includes(commandId)
        ? prev.filter(id => id !== commandId)
        : [...prev, commandId]
    );
  }, []);

  // Sélectionner/Désélectionner toutes les commandes non payées
  const toggleAllCommandsSelection = useCallback(() => {
    if (!relatedCommandsData?.commandes) return;

    const unpaidCommands = relatedCommandsData.commandes.filter(
      cmd => cmd.statut_paiement !== 2 && cmd.statut_paiement !== '2'
    );

    const unpaidIds = unpaidCommands.map(cmd => cmd.id_commande);
    const allSelected = unpaidIds.every(id => selectedCommandsIds.includes(id));

    if (allSelected) {
      setSelectedCommandsIds([]);
    } else {
      setSelectedCommandsIds(unpaidIds);
    }
  }, [relatedCommandsData, selectedCommandsIds]);

  // Calculer les totaux pour l'affichage
  const paymentSummary = useMemo(() => {
    if (!relatedCommandsData?.commandes) {
      return { total: 0, paid: 0, pending: 0, selected: 0, remaining: 0 };
    }

    let total = 0;
    let paid = 0;
    let pending = 0;
    let selected = 0;

    relatedCommandsData.commandes.forEach(cmd => {
      const cmdTotal = cmd.details?.reduce((acc, art) => acc + (art.prix_total || 0), 0) || cmd.prix || 0;
      total += cmdTotal;

      if (cmd.statut_paiement === 2 || cmd.statut_paiement === '2') {
        paid += cmdTotal;
      } else {
        pending += cmdTotal;
        if (selectedCommandsIds.includes(cmd.id_commande)) {
          selected += cmdTotal;
        }
      }
    });

    const remaining = Math.max(0, pending - selected);

    return { total, paid, pending, selected, remaining };
  }, [relatedCommandsData, selectedCommandsIds]);

  const fetchRelatedCommands = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.access_token;
      const user = JSON.parse(localStorage.getItem("user"));

      if (!selectedCommand?.table_restaurant?.numero_table) return;

      const numero_table = selectedCommand.table_restaurant.numero_table;

      const response = await axios.get(
        `${base_api_url}api/commande_table/${numero_table}/${user.restoId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );

      if (
        response.data &&
        Array.isArray(response.data.commandes) &&
        response.data.commandes.length > 0
      ) {
        setRelatedCommandsData(response.data);
        setShowRelatedCommands(true);
      }
    } catch (error) {
      console.error("Erreur fetchRelatedCommands:", error);
      showErrorToast("Impossible de charger les commandes associées");
    }
  };

  // Fonction pour encaisser les commandes groupées (nouvelle version)
  const handleGroupPayment = async () => {
    if (selectedCommandsIds.length === 0) {
      showErrorToast("Veuillez sélectionner au moins une commande");
      return;
    }

    // Confirmation avant encaissement
    const result = await MySwal.fire({
      title: "Encaisser les commandes sélectionnées ?",
      html: `
        <div style="text-align: center; padding: 20px;">
          <p style="margin-bottom: 12px;"><strong>Commandes sélectionnées:</strong> ${selectedCommandsIds.length}</p>
          <p style="margin-bottom: 12px;"><strong>Montant à encaisser:</strong> ${formatPrice(paymentSummary.selected)}</p>
          <p style="color: #6b7280; font-size: 14px;">Le statut de paiement sera mis à jour pour toutes les commandes sélectionnées.</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "✓ Oui, encaisser",
      cancelButtonText: "✕ Annuler",
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#6b7280",
      width: '450px',
    });

    if (!result.isConfirmed) return;

    setProcessingGroupPayment(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.access_token;

      const response = await axios.put(
        `${base_api_url}api/commandes/paiement-groupe`,
        { commandes_ids: selectedCommandsIds },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.status === 200 || response.status === 201) {
        showSuccessToast("Encaissement effectué avec succès !");

        // Réinitialiser la sélection
        setSelectedCommandsIds([]);

        // Rafraîchir les données
        await fetchCommands();

        // Recharger les commandes relatives
        if (selectedCommand?.reference) {
          await fetchRelatedCommands(selectedCommand.reference);
        }
      }
    } catch (error) {
      console.error("Erreur lors de l'encaissement groupé:", error);
      showErrorToast(
        error.response?.data?.message ||
        "Une erreur est survenue lors de l'encaissement"
      );
    } finally {
      setProcessingGroupPayment(false);
    }
  };

  // Fonction pour encaisser les commandes groupées (ancienne version - conservée pour compatibilité)
  // const handleGroupPaymentLegacy = async () => {
  //   if (!relatedCommandsData?.commandes || relatedCommandsData.commandes.length === 0) {
  //     showErrorToast("Aucune commande à encaisser");
  //     return;
  //   }

  //   // Récupérer le refgroupe depuis la première commande
  //   const refgroupe = relatedCommandsData.commandes[0]?.refgroupe;

  //   if (!refgroupe) {
  //     showErrorToast("Référence de groupe introuvable");
  //     return;
  //   }

  //   // Confirmation avant encaissement
  //   const result = await MySwal.fire({
  //     title: "Encaisser les commandes groupées ?",
  //     html: `
  //       <div style="text-align: center;">
  //         <p><strong>Table:</strong> ${relatedCommandsData?.table_restaurant?.numero_table || "N/A"}</p>
  //         <p><strong>Nombre de commandes:</strong> ${relatedCommandsData.commandes.length}</p>
  //         <p><strong>Total prévisionnel:</strong> ${formatPrice(relatedCommandsData?.prix_total_previsionnel)}</p>
  //       </div>
  //     `,
  //     icon: "question",
  //     showCancelButton: true,
  //     confirmButtonText: "Oui, encaisser",
  //     cancelButtonText: "Annuler",
  //     confirmButtonColor: "#10b981",
  //     cancelButtonColor: "#6b7280",
  //   });

  //   if (!result.isConfirmed) return;

  //   setProcessingGroupPayment(true);
  //   try {
  //     const token = JSON.parse(localStorage.getItem("user"))?.access_token;

  //     const response = await axios.put(
  //       `${base_api_url}api/commande_encaisse_grouper/${refgroupe}`,
  //       {},
  //       { headers: { Authorization: `Bearer ${token}` } },
  //     );

  //     if (response.status === 200 || response.status === 201) {
  //       showSuccessToast("Encaissement groupé effectué avec succès !");

  //       // Rafraîchir les données
  //       await fetchCommands();

  //       // Fermer le modal
  //       handleCloseModal();
  //     }
  //   } catch (error) {
  //     console.error("Erreur lors de l'encaissement groupé:", error);
  //     showErrorToast(
  //       error.response?.data?.message ||
  //       "Une erreur est survenue lors de l'encaissement groupé"
  //     );
  //   } finally {
  //     setProcessingGroupPayment(false);
  //   }
  // };

  // === STATUS PROGRESSION FUNCTIONS ===

  // Fonction pour valider ou rejeter une commande (statut 10)
  const validateOrRejectCommand = async (reference, newStatus, action) => {
    setUpdatingStatus(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.access_token;
      const userId = JSON.parse(localStorage.getItem("user"))?.id_utilisateur;

      const payload = {
        statut: newStatus,
        id_operator: userId,
      };

      const response = await axios.put(
        `${base_api_url}api/commande/${reference}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.status === 200 || response.status === 201) {
        const actionText = action === "validate" ? "validée" : "rejetée";
        showSuccessToast(`La commande a été ${actionText} avec succès.`);
        handleCloseModal();
        await fetchCommands();
      }
    } catch (error) {
      console.error(
        `Error ${action === "validate" ? "validating" : "rejecting"} command:`,
        error,
      );

      let errorMessage = `Impossible de ${action === "validate" ? "valider" : "rejeter"} la commande.`;

      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        switch (status) {
          case 400:
            errorMessage =
              errorData?.message || "Les données envoyées ne sont pas valides.";
            break;
          case 401:
            errorMessage = "Votre session a expiré. Veuillez vous reconnecter.";
            setTimeout(() => {
              window.location.href = "/login";
            }, 2000);
            break;
          case 403:
            errorMessage =
              "Vous n'avez pas les permissions nécessaires pour cette action.";
            break;
          case 404:
            errorMessage = "La commande n'existe plus ou a été supprimée.";
            break;
          case 409:
            errorMessage =
              "La commande a déjà été modifiée. Veuillez actualiser.";
            break;
          case 422:
            errorMessage =
              "L'action demandée n'est pas valide pour cette commande.";
            break;
          case 500:
            errorMessage = "Erreur serveur. Veuillez réessayer.";
            break;
          default:
            errorMessage =
              errorData?.message || "Une erreur inattendue s'est produite.";
        }
      } else if (error.request) {
        errorMessage =
          "Impossible de contacter le serveur. Vérifiez votre connexion.";
      }

      showErrorToast(errorMessage);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const validatePayment = async (reference) => {
    setUpdatingStatus(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.access_token;

      const response = await axios.put(
        `${base_api_url}api/commandes/${reference}`,
        { statut_paiement: 2 },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Handle successful responses (200, 201)
      if (response.status === 200 || response.status === 201) {
        MySwal.fire({
          icon: "success",
          // title: "Paiement validé !",
          text: "Le paiement a été confirmé avec succès.",
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "pixel-toast",
          },
        });

        handleCloseModal();
        await fetchCommands();
      }
    } catch (error) {
      console.error("Error validating payment:", error);

      //       let errorTitle = "Erreur de validation du paiement";
      let errorMessage = "Impossible de valider le paiement.";

      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        switch (status) {
          case 400:
            //             errorTitle = "Données invalides";
            errorMessage =
              errorData?.message || "Les données envoyées ne sont pas valides.";
            break;

          case 401:
            //             errorTitle = "Session expirée";
            errorMessage = "Votre session a expiré. Veuillez vous reconnecter.";
            setTimeout(() => {
              navigate(route.signin);
            }, 2000);
            break;

          case 403:
            //             errorTitle = "Accès refusé";
            errorMessage =
              "Vous n'avez pas les permissions nécessaires pour valider ce paiement.";
            break;

          case 404:
            //             errorTitle = "Commande introuvable";
            errorMessage =
              "La commande que vous tentez de modifier n'existe plus ou a été supprimée.";
            break;

          case 409:
            //             errorTitle = "Paiement déjà validé";
            errorMessage = "Le paiement a déjà été validé pour cette commande.";
            break;

          case 422:
            //             errorTitle = "Validation impossible";
            errorMessage =
              "Le paiement ne peut pas être validé dans l'état actuel de la commande.";
            break;

          case 500:
            //             errorTitle = "Erreur du serveur";
            errorMessage =
              "Une erreur s'est produite sur le serveur. Veuillez réessayer.";
            break;

          case 503:
            //             errorTitle = "Service indisponible";
            errorMessage =
              "Le service est temporairement indisponible. Veuillez réessayer plus tard.";
            break;

          default:
            //             errorTitle = `Erreur ${status}`;
            errorMessage =
              errorData?.message || "Une erreur inattendue s'est produite.";
        }
      } else if (error.request) {
        //         errorTitle = "Problème de connexion";
        errorMessage =
          "Impossible de contacter le serveur. Vérifiez votre connexion internet.";
      }

      MySwal.fire({
        icon: "error",
        // title: errorTitle,
        text: errorMessage,
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
        customClass: {
          popup: "pixel-toast",
        },
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Fonction pour faire progresser une commande (statut 3, 4, 5)
  const markAsReady = async (reference, newStatus) => {
    setUpdatingStatus(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.access_token;
      const userId = JSON.parse(localStorage.getItem("user"))?.id_utilisateur;

      const response = await axios.put(
        `${base_api_url}api/commande/${reference}`,
        {
          statut: newStatus,
          id_operator: userId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.status === 200 || response.status === 201) {
        let message = "Commande mise à jour avec succès.";

        if (newStatus === 4) {
          message = "Commande prête à être livré.";
        } else if (newStatus === 5) {
          message = "Commande marquée comme livré.";
        }

        showSuccessToast(message);
        handleCloseModal();
        await fetchCommands();
      }
    } catch (error) {
      console.error("Error updating command status:", error);

      let errorMessage = "Impossible de mettre à jour le statut.";

      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        switch (status) {
          case 400:
            errorMessage = errorData?.message || "Données invalides.";
            break;
          case 401:
            errorMessage = "Session expirée. Reconnexion nécessaire.";
            setTimeout(() => {
              window.location.href = "/login";
            }, 2000);
            break;
          case 403:
            errorMessage = "Vous n'avez pas les permissions nécessaires.";
            break;
          case 404:
            errorMessage = "La commande n'existe plus.";
            break;
          case 409:
            errorMessage =
              "La commande a déjà été modifiée. Actualisez la page.";
            break;
          case 422:
            errorMessage = "Le statut demandé n'est pas valide.";
            break;
          case 500:
            errorMessage = "Erreur serveur. Réessayez.";
            break;
          default:
            errorMessage = errorData?.message || "Erreur inattendue.";
        }
      } else if (error.request) {
        errorMessage = "Impossible de contacter le serveur.";
      }

      showErrorToast(errorMessage);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Calcul des compteurs d'onglets
  const getTabCount = (tabLabel) => {
    try {
      if (tabLabel === "Toutes") return commands.length;

      return commands.filter((cmd) => {
        if (!cmd || typeof cmd.statut === "undefined") return false;

        if (tabLabel === "À valider") return cmd.statut === 10;
        if (tabLabel === "En préparation")
          return cmd.statut === 2 || cmd.statut === 3;
        if (tabLabel === "À servir")
          return cmd.statut === 4;
        if (tabLabel === "Livré")
          return cmd.statut === 5 && cmd.statut_paiement === 1;
        if (tabLabel === "Terminée")
          return cmd.statut === 5 && cmd.statut_paiement === 2;
        if (tabLabel === "Annulée")
          return cmd.statut === 0;

        return statusMap[cmd.statut] === tabLabel || cmd.statut === tabLabel;
      }).length;
    } catch (error) {
      console.error("Erreur calcul compteur:", error);
      return 0;
    }
  };

  // Configuration des onglets
  const tabs = [
    { label: "Toutes", color: "orange" },
    { label: "À valider", color: "red" },
    { label: "En préparation", color: "orange" },
    { label: "À servir", color: "green" },
    { label: "Livré", color: "blue" },
    { label: "Terminée", color: "green" },
    { label: "Annulée", color: "grey" },
  ];

  // Formatage sécurisé du prix
  const formatPrice = (price) => {
    try {
      const d = JSON.parse(localStorage.getItem('restaurant'))?.devise?.toUpperCase() || "EUR";
      if (price === null || price === undefined) return "--";
      const numPrice = typeof price === "string" ? parseFloat(price) : price;
      return isNaN(numPrice)
        ? "--"
        : `${numPrice.toLocaleString("fr-FR")} ${d === "XAF" ? "FCFA" : d === "XOF" ? "CFA" : d === "USD" ? "$" : d === "EUR" ? "€" : d}`;
    } catch (error) {
      console.error("Erreur formatage prix:", error);
      return "--";
    }
  };

  // Formatage sécurisé de la date
  const formatDate = (dateString) => {
    try {
      if (!dateString) return "--";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "--";

      return date.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Erreur formatage date:", error);
      return "--";
    }
  };

  return (
    <>
      <SessionControl
        storageKey="user"
        propsup="email"
        redirectTo={route.signin}
      />
      <AbonnementControl storageKey="user" redirectTo={all_routes.pricing} />
      <div className="page-wrapper">
        <div className="commands-page">
          {/* HEADER */}
          <div className="commands-header">
            <div className="header-left">
              <h2>Commandes</h2>
              <p>Gérer les commandes de {restaurantName}</p>
            </div>
            <div className="header-actions">
              <button
                className="btn-refresh"
                onClick={handleRefresh}
                disabled={loading}
                aria-label="Actualiser les commandes"
              >
                <RefreshCw size={16} className={loading ? "spinning" : ""} />
                <span className="btn-text">Actualiser</span>
              </button>
              <button className="btn-new-command" aria-label="Nouvelle commande">
                <Plus size={16} />
                <span className="btn-text">Nouvelle commande</span>
              </button>
            </div>
          </div>

          {/* SEARCH BAR */}
          <div className="search-container">
            <Search className="search-icon-large" size={20} />
            <input
              type="text"
              className="search-input-large"
              placeholder="Rechercher par numéro, serveur ou table..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Rechercher une commande"
            />

            {/* PAYMENT STATUS FILTER DROPDOWN */}
            <select
              className="payment-status-filter"
              value={paymentStatusFilter}
              onChange={(e) => setPaymentStatusFilter(e.target.value)}
              aria-label="Filtrer par statut de paiement"
            >
              <option value="Tous">Tous les paiements</option>
              <option value="À encaisser">À encaisser</option>
              <option value="Payé">Payé</option>
            </select>
          </div>

          {/* TABS */}
          <div className="tabs-container">
            {tabs.map((tab) => {
              const count = getTabCount(tab.label);
              return (
                <button
                  key={tab.label}
                  className={`tab-pill ${activeTab === tab.label ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.label)}
                  aria-label={`Filtrer par ${tab.label}`}
                  aria-pressed={activeTab === tab.label}
                >
                  <span className={`status-dot dot-${tab.color}`}></span>
                  <span className="tab-label">{tab.label}</span>
                  <span className="tab-count">({count})</span>
                </button>
              );
            })}
          </div>

          {/* COMMANDS GRID */}
          <div className="commands-grid">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Chargement des commandes...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <p>{error}</p>
                <button onClick={handleRefresh} className="retry-btn">
                  Réessayer
                </button>
              </div>
            ) : filteredCommands.length === 0 ? (
              <div className="empty-state">
                <p>Aucune commande trouvée</p>
              </div>
            ) : (
              filteredCommands.map((cmd) => {
                try {
                  const statusLabel = getStatusLabel(
                    cmd.statut,
                    cmd.statut_paiement,
                  );
                  const statusColor =
                    statusColors[statusLabel] || statusColors["À valider"];
                  const tableType = getTableType(
                    cmd.table_restaurant?.type_table,
                  );

                  return (
                    <div
                      key={cmd.id_commande || cmd.reference}
                      className="command-card"
                      onClick={() => handleOpenModal(cmd)}
                      style={{ borderLeft: `4px solid ${statusColor.border}` }}
                    >
                      <button
                        className="card-eye-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenModal(cmd);
                        }}
                        aria-label="Voir les détails"
                      >
                        <Eye size={18} />
                      </button>

                      <div className="card-header-row">
                        <div className="cmd-id-group">
                          <span className="cmd-id">
                            {/* {cmd.reference || `#${cmd.id_commande}` || "--"} */}
                            {(() => {
                              const ref = cmd.reference || cmd.id_commande?.toString() || "--";
                              return ref.includes("-") ? ref.split("-").pop() : ref.replace("#", "");
                            })()}
                          </span>
                          {statusLabel !== "À encaisser" && (
                            <span
                              className="status-badge"
                              style={{
                                backgroundColor: statusColor.bg,
                                color: statusColor.text,
                                border: `1px solid ${statusColor.border}`,
                              }}
                            >
                              {statusLabel}
                            </span>
                          )}
                        </div>
                        <span className="cmd-price">
                          {formatPrice(
                            cmd.details.reduce(
                              (total, item) => total + item.prix,
                              0,
                            ) || cmd.prix,
                          )}
                        </span>
                      </div>

                      <div className="card-subinfo-row">
                        <span className="payment-method">
                          {tableType || cmd.moyen_paiement || "--"}
                        </span>
                        <span className="subinfo-separator">•</span>
                        <div className="date-group">
                          <Clock size={14} />
                          <span>{formatDate(cmd.date_commande)}</span>
                        </div>
                      </div>

                      <div className="card-grid-info">
                        <div className="info-block">
                          <h4>Serveur</h4>
                          <div className="info-value">
                            <User size={16} />
                            <span>{cmd.nom || "--"}</span>
                          </div>
                        </div>
                        <div className="info-block">
                          <h4>Table</h4>
                          <div className="info-value">
                            <MapPin size={16} />
                            <span>
                              {cmd.numtable ||
                                cmd.table_restaurant?.numero_table ||
                                "--"}
                            </span>
                          </div>
                        </div>
                        <div className="info-block">
                          <h4>Articles</h4>
                          <div className="info-value">
                            <Grid size={16} />
                            <span>
                              {Array.isArray(cmd.details)
                                ? cmd.details.length
                                : "--"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="card-footer-details">
                        <HelpCircle size={16} />
                        <span>
                          {Array.isArray(cmd.details) && cmd.details.length > 0
                            ? cmd.details
                              .map((art, idx) => {
                                const nom = art.article?.nom || "Article";
                                const qte =
                                  art.quantite || art.article?.quantite || "1";
                                return `${qte} × ${nom}${idx < cmd.details.length - 1 ? ", " : ""}`;
                              })
                              .join("")
                            : "Aucun article"}
                        </span>
                      </div>

                      {/* Payment Status Badge */}
                      {cmd.statut !== 0 && (
                        <div className={`payment-status-badge ${cmd.statut_paiement === 2 ? 'badge-paid' : 'badge-unpaid'}`}>
                          {cmd.statut_paiement === 2 ? 'Payé' : 'À encaisser'}
                        </div>
                      )}
                    </div>
                  );
                } catch (error) {
                  console.error("Erreur rendu carte:", error, cmd);
                  return null;
                }
              })
            )}
          </div>

          {/* COMMAND DETAILS MODAL */}
          {isModalOpen && selectedCommand && (
            <div className="modal-overlay" onClick={handleCloseModal}>
              <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="modal-header">
                  <h2 className="modal-title">
                    {(() => {
                      const ref = selectedCommand.reference || selectedCommand.id_commande?.toString() || "";
                      return ref.includes("-") ? ref.split("-").pop() : ref.replace("#", "");
                    })()}
                  </h2>
                  <button
                    className="modal-close-btn"
                    onClick={handleCloseModal}
                    aria-label="Fermer"
                  >
                    <X size={24} />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="modal-content">
                  {!showRelatedCommands ? (
                    <>
                      {/* Status Badges */}
                      <div className="modal-badges">
                        <span
                          className="modal-status-badge"
                          style={{
                            backgroundColor:
                              statusColors[
                                getStatusLabel(
                                  selectedCommand.statut,
                                  selectedCommand.statut_paiement,
                                )
                              ]?.bg,
                            color:
                              statusColors[
                                getStatusLabel(
                                  selectedCommand.statut,
                                  selectedCommand.statut_paiement,
                                )
                              ]?.text,
                          }}
                        >
                          {getStatusLabel(
                            selectedCommand.statut,
                            selectedCommand.statut_paiement,
                          )}
                        </span>
                        <span className="modal-type-badge">
                          {getTableType(
                            selectedCommand.table_restaurant?.type_table,
                          ) || "Client"}
                        </span>
                      </div>

                      {/* Toggle button for related commands - only for QR Code tables */}
                      {!showRelatedCommands &&
                        relatedCommandsData &&
                        relatedCommandsData.commandes &&
                        relatedCommandsData.commandes.length > 1 && (
                          <button
                            className="btn-show-related"
                            onClick={handleShowRelatedCommands}
                          >
                            <Eye size={16} />
                            Voir les commandes relatives (
                            {relatedCommandsData.commandes.length - 1} autres)
                          </button>
                        )}

                      {/* Date and Server Info */}
                      <div className="modal-info-row">
                        <div className="modal-info-item">
                          <Clock size={16} />
                          <span>{formatDate(selectedCommand.date_commande)}</span>
                        </div>
                        <div className="modal-info-item">
                          <User size={16} />
                          <span>
                            {getTableType(
                              selectedCommand.table_restaurant?.type_table,
                            ) === "Livraison"
                              ? "Livraison"
                              : getTableType(
                                selectedCommand.table_restaurant?.type_table,
                              ) === "Classique"
                                ? selectedCommand.table_restaurant
                                  ?.numero_table ||
                                selectedCommand.numtable ||
                                "--"
                                : selectedCommand.nom || "Client"}
                          </span>
                        </div>
                      </div>

                      {/* Articles Section */}
                      <div className="modal-section">
                        <h3 className="modal-section-title">
                          <Grid size={16} />
                          Articles (
                          {Array.isArray(selectedCommand.details)
                            ? selectedCommand.details.length
                            : 0}
                          )
                        </h3>
                        <div className="modal-articles-list">
                          {Array.isArray(selectedCommand.details) &&
                            selectedCommand.details.length > 0 ? (
                            selectedCommand.details.map((art, idx) => (
                              <div key={idx} className="modal-article-item">
                                <div className="modal-article-info">
                                  <span className="modal-article-qty">
                                    {art.quantite || art.article?.quantite || "1"}{" "}
                                    ×
                                  </span>
                                  <div className="modal-article-details">
                                    <span className="modal-article-name">
                                      {art.article?.nom || "Article"}
                                    </span>
                                    {art.article?.etiquette && (
                                      <span className="modal-article-tag">
                                        {art.article.etiquette}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <span className="modal-article-price">
                                  {formatPrice(
                                    art.prix_total || art.prix_unitaire,
                                  )}
                                </span>
                              </div>
                            ))
                          ) : (
                            <p className="modal-empty">Aucun article</p>
                          )}
                        </div>
                      </div>

                      {/* Summary Section */}
                      <div className="modal-section">
                        <h3 className="modal-section-title">Récapitulatif</h3>
                        <div className="modal-summary">
                          <div className="modal-summary-row">
                            <span>Sous-total</span>
                            <span>{formatPrice(selectedCommand.prix)}</span>
                          </div>
                          <div className="modal-summary-row modal-summary-total">
                            <span>Total</span>
                            <span className="modal-total-price">
                              {formatPrice(
                                selectedCommand.details.reduce(
                                  (total, item) => total + item.prix,
                                  0,
                                ) || selectedCommand.prix,
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Delivery Address Section - Only show if exists */}
                      {selectedCommand.adresse_livraison && Object.keys(selectedCommand.adresse_livraison).length > 0 && (
                        <div className="modal-section">
                          <h3 className="modal-section-title">
                            <MapPin size={16} />
                            Adresse de livraison
                          </h3>
                          <div className="delivery-address-content" style={{
                            backgroundColor: '#f8f9fa',
                            padding: '16px',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef'
                          }}>
                            {selectedCommand.adresse_livraison.nom && (
                              <div style={{ marginBottom: '8px' }}>
                                <strong style={{ color: '#495057', fontSize: '14px' }}>
                                  <User size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                  {selectedCommand.adresse_livraison.nom}
                                </strong>
                              </div>
                            )}

                            {selectedCommand.adresse_livraison.telephone && (
                              <div style={{ marginBottom: '8px', color: '#6c757d', fontSize: '13px' }}>
                                <span style={{ fontWeight: '500' }}>Tél:</span> {selectedCommand.adresse_livraison.telephone}
                              </div>
                            )}

                            {selectedCommand.adresse_livraison.email && (
                              <div style={{ marginBottom: '8px', color: '#6c757d', fontSize: '13px' }}>
                                <span style={{ fontWeight: '500' }}>Email:</span> {selectedCommand.adresse_livraison.email}
                              </div>
                            )}

                            {selectedCommand.adresse_livraison.adresse && (
                              <div style={{
                                marginTop: '12px',
                                paddingTop: '12px',
                                borderTop: '1px solid #dee2e6',
                                color: '#212529',
                                fontSize: '13px',
                                lineHeight: '1.6'
                              }}>
                                <MapPin size={14} style={{ marginRight: '6px', verticalAlign: 'middle', color: '#6c757d' }} />
                                {selectedCommand.adresse_livraison.adresse}
                                {selectedCommand.adresse_livraison.complement && (
                                  <span style={{ display: 'block', marginTop: '4px', marginLeft: '20px', color: '#6c757d' }}>
                                    {selectedCommand.adresse_livraison.complement}
                                  </span>
                                )}
                              </div>
                            )}

                            {(selectedCommand.adresse_livraison.ville || selectedCommand.adresse_livraison.code_postal) && (
                              <div style={{ marginTop: '8px', color: '#6c757d', fontSize: '13px' }}>
                                {selectedCommand.adresse_livraison.ville}
                                {selectedCommand.adresse_livraison.ville && selectedCommand.adresse_livraison.code_postal && ', '}
                                {selectedCommand.adresse_livraison.code_postal}
                              </div>
                            )}

                            {selectedCommand.adresse_livraison.autre && (
                              <div style={{
                                marginTop: '12px',
                                padding: '8px 12px',
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffc107',
                                borderRadius: '6px',
                                color: '#856404',
                                fontSize: '12px',
                                fontStyle: 'italic'
                              }}>
                                <strong>Note:</strong> {selectedCommand.adresse_livraison.autre}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* History Section */}
                      <div className="modal-section">
                        <h3 className="modal-section-title">Historique</h3>
                        <div className="modal-history">
                          {selectedCommand.date_commande && (
                            <div className="modal-history-item">
                              <span className="modal-history-dot"></span>
                              <div className="modal-history-content">
                                <span className="modal-history-label">
                                  Commande créée
                                </span>
                                <span className="modal-history-time">
                                  {formatDate(selectedCommand.date_commande)}
                                </span>
                              </div>
                            </div>
                          )}
                          {selectedCommand.date_validation_serveuse && (
                            <div className="modal-history-item">
                              <span className="modal-history-dot"></span>
                              <div className="modal-history-content">
                                <span className="modal-history-label">
                                  Envoyée en cuisine
                                </span>
                                <span className="modal-history-time">
                                  {formatDate(
                                    selectedCommand.date_validation_serveuse,
                                  )}
                                </span>
                              </div>
                            </div>
                          )}
                          {selectedCommand.date_fin_preparation && (
                            <div className="modal-history-item">
                              <span className="modal-history-dot"></span>
                              <div className="modal-history-content">
                                <span className="modal-history-label">
                                  Prête à servir
                                </span>
                                <span className="modal-history-time">
                                  {formatDate(
                                    selectedCommand.date_fin_preparation,
                                  )}
                                </span>
                              </div>
                            </div>
                          )}
                          {selectedCommand.statut === 5 && (
                            <div className="modal-history-item">
                              <span className="modal-history-dot"></span>
                              <div className="modal-history-content">
                                <span className="modal-history-label">
                                  Livré
                                </span>
                                <span className="modal-history-time">
                                  {formatDate(
                                    selectedCommand.date_validation_client ||
                                    selectedCommand.date_commande,
                                  )}
                                </span>
                              </div>
                            </div>
                          )}
                          {selectedCommand.date_payer && (
                            <div className="modal-history-item">
                              <span className="modal-history-dot"></span>
                              <div className="modal-history-content">
                                <span className="modal-history-label">Payée</span>
                                <span className="modal-history-time">
                                  {formatDate(selectedCommand.date_payer)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* 🆕 RELATED COMMANDS VIEW - MODERN DESIGN */}
                      <div className="related-commands-modern-view">
                        {/* Back Button */}
                        <button
                          className="btn-back-modern"
                          onClick={handleBackToMainCommand}
                        >
                          <ArrowLeft size={16} />
                          Retour à la commande
                        </button>

                        {/* Sticky Summary Header */}
                        <div className="sticky-summary-header">
                          <h3 className="summary-title">
                            Commandes de la table{" "}
                            {relatedCommandsData?.table_restaurant?.numero_table}
                          </h3>

                          {/* Summary Cards */}
                          <div className="summary-cards-grid">
                            <div className="summary-card consumed">
                              <span className="summary-card-label">RÉGLÉ</span>
                              <div className="summary-card-value">
                                {formatPrice(paymentSummary.paid)}
                              </div>
                            </div>
                            <div className="summary-card pending">
                              <span className="summary-card-label">À RÉGLER</span>
                              <div className="summary-card-value">
                                {formatPrice(paymentSummary.pending)}
                              </div>
                            </div>
                          </div>

                          {/* Selection Summary */}
                          <div className="selection-summary-row">
                            <div className="selection-info selected">
                              <span className="selection-label">Ma sélection</span>
                              <span className={`selection-amount ${paymentSummary.selected > 0 ? 'active' : ''}`}>
                                {formatPrice(paymentSummary.selected)}
                              </span>
                            </div>
                            <div className="selection-info remaining">
                              <span className="selection-label">Solde restant</span>
                              <div className="selection-amount-with-icon">
                                <Calculator size={14} />
                                <span className="selection-amount">
                                  {formatPrice(paymentSummary.remaining)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="action-buttons-row">
                            <button
                              onClick={toggleAllCommandsSelection}
                              className={`btn-toggle-all ${selectedCommandsIds.length > 0 ? 'active' : ''
                                }`}
                              disabled={processingGroupPayment}
                            >
                              {selectedCommandsIds.length > 0
                                ? 'Tout décocher'
                                : 'Tout cocher'}
                            </button>
                            <button
                              disabled={selectedCommandsIds.length === 0 || processingGroupPayment}
                              onClick={handleGroupPayment}
                              className="btn-pay-selected"
                            >
                              {processingGroupPayment ? (
                                <>
                                  <RefreshCw size={16} className="spinning" />
                                  Traitement...
                                </>
                              ) : (
                                <>
                                  <CreditCard size={16} />
                                  Régler {paymentSummary.selected > 0 ? formatPrice(paymentSummary.selected) : ''}
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Commands List */}
                        <div className="commands-list-modern">
                          {relatedCommandsData?.commandes &&
                            relatedCommandsData.commandes.map((cmd) => {
                              const statusLabel = getStatusLabel(
                                cmd.statut,
                                cmd.statut_paiement,
                              );
                              const statusColor =
                                statusColors[statusLabel] ||
                                statusColors["À valider"];
                              const isCurrentCommand =
                                cmd.id_commande === selectedCommand?.id_commande;
                              const isPaid = cmd.statut_paiement === 2 || cmd.statut_paiement === '2';
                              const isExpanded = expandedCommands.includes(cmd.id_commande);
                              const isSelected = selectedCommandsIds.includes(cmd.id_commande);
                              const cmdTotal = cmd.details?.reduce((acc, art) => acc + (art.prix_total || 0), 0) || cmd.prix || 0;

                              return (
                                <div
                                  key={cmd.id_commande}
                                  className={`command-card-modern ${isPaid ? 'paid' : ''
                                    } ${isCurrentCommand ? 'current' : ''}`}
                                >
                                  {/* Card Header */}
                                  <div className="command-card-header">
                                    <div className="card-header-left">
                                      {/* Checkbox */}
                                      {!isPaid && (
                                        <button
                                          onClick={() => toggleCommandSelection(cmd.id_commande, isPaid)}
                                          className={`checkbox-btn ${isSelected ? 'checked' : ''}`}
                                          disabled={processingGroupPayment}
                                          aria-label={isSelected ? "Décocher" : "Cocher"}
                                        >
                                          {isSelected && <Check size={14} />}
                                        </button>
                                      )}

                                      {/* Command Info */}
                                      <div
                                        className="command-info-section"
                                        onClick={() => toggleCommandExpansion(cmd.id_commande)}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <div className="command-ref-row">
                                          <span className="command-ref">
                                            {cmd.reference || `#${cmd.id_commande}`}
                                          </span>
                                          <span className="command-time">
                                            {new Date(cmd.date_commande).toLocaleTimeString('fr-FR', {
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            })}
                                          </span>
                                          {isCurrentCommand && (
                                            <span className="current-badge-small">Actuelle</span>
                                          )}
                                        </div>
                                        <div className="command-status-badge-container">
                                          <span
                                            className="command-status-badge"
                                            style={{
                                              backgroundColor: statusColor.bg,
                                              color: statusColor.text,
                                            }}
                                          >
                                            {statusLabel}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="card-header-right">
                                      <span className="command-total-price">
                                        {formatPrice(cmdTotal)}
                                      </span>
                                      <button
                                        onClick={() => toggleCommandExpansion(cmd.id_commande)}
                                        className="expand-btn"
                                        aria-label={isExpanded ? "Réduire" : "Développer"}
                                      >
                                        {isExpanded ? (
                                          <ChevronUp size={18} />
                                        ) : (
                                          <ChevronDown size={18} />
                                        )}
                                      </button>
                                    </div>
                                  </div>

                                  {/* Expanded Content */}
                                  {isExpanded && (
                                    <div className="command-card-content">
                                      {/* Command Meta Info */}
                                      <div className="command-meta-info">
                                        <div className="meta-info-item">
                                          <Clock size={14} />
                                          <span>{formatDate(cmd.date_commande)}</span>
                                        </div>
                                        <div className="meta-info-item">
                                          <User size={14} />
                                          <span>{cmd.nom || "--"}</span>
                                        </div>
                                      </div>

                                      {/* Articles List */}
                                      <div className="articles-list-section">
                                        <h4 className="articles-list-title">
                                          <Grid size={14} />
                                          Articles ({cmd.details?.length || 0})
                                        </h4>
                                        <div className="articles-list">
                                          {cmd.details && cmd.details.length > 0 ? (
                                            cmd.details.map((art, idx) => (
                                              <div
                                                key={idx}
                                                className="article-item-modern"
                                              >
                                                <div className="article-info">
                                                  <span className="article-qty">
                                                    x{art.quantite || "1"}
                                                  </span>
                                                  <span className="article-name">
                                                    {art.article?.nom || "Article"}
                                                  </span>
                                                </div>
                                                <span className="article-price">
                                                  {formatPrice(art.prix_total || art.prix_unitaire)}
                                                </span>
                                              </div>
                                            ))
                                          ) : (
                                            <p className="articles-empty">Aucun article</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* ACTION BUTTONS FOOTER */}
                <div className="modal-actions-footer">
                  {/* Buttons for status 10 (À valider) */}
                  {selectedCommand.statut === 10 && (
                    <>
                      <button
                        className="modal-action-btn btn-reject"
                        onClick={() =>
                          validateOrRejectCommand(
                            selectedCommand.reference,
                            0,
                            "reject",
                          )
                        }
                        disabled={updatingStatus}
                      >
                        {updatingStatus ? (
                          <>
                            <div className="btn-spinner"></div>
                            Rejet...
                          </>
                        ) : (
                          <>
                            <XCircle size={18} />
                            Rejeter
                          </>
                        )}
                      </button>
                      <button
                        className="modal-action-btn btn-validate"
                        onClick={() =>
                          validateOrRejectCommand(
                            selectedCommand.reference,
                            3,
                            "validate",
                          )
                        }
                        disabled={updatingStatus}
                      >
                        {updatingStatus ? (
                          <>
                            <div className="btn-spinner"></div>
                            Validation...
                          </>
                        ) : (
                          <>
                            <Check size={18} />
                            Valider
                          </>
                        )}
                      </button>
                    </>
                  )}

                  {/* Button for status 3 (En préparation) -> 4 (À servir) */}
                  {selectedCommand.statut === 3 && (
                    <button
                      className="modal-action-btn btn-ready"
                      onClick={() => markAsReady(selectedCommand.reference, 4)}
                      disabled={updatingStatus}
                    >
                      {updatingStatus ? (
                        <>
                          <div className="btn-spinner"></div>
                          Mise à jour...
                        </>
                      ) : (
                        <>
                          <Check size={18} />
                          Prête à servir
                        </>
                      )}
                    </button>
                  )}

                  {/* Button for status 4 (À servir) -> 5 (Livré/À encaisser) */}
                  {selectedCommand.statut === 4 && (
                    <button
                      className="modal-action-btn btn-served"
                      onClick={() => markAsReady(selectedCommand.reference, 5)}
                      disabled={updatingStatus}
                    >
                      {updatingStatus ? (
                        <>
                          <div className="btn-spinner"></div>
                          Mise à jour...
                        </>
                      ) : (
                        <>
                          <Check size={18} />
                          Marquer comme livré
                        </>
                      )}
                    </button>
                  )}

                  {selectedCommand.statut !== 10 &&
                    (selectedCommand.statut_paiement == "1" ||
                      selectedCommand.statut_paiement === null) && (
                      <button
                        className="modal-action-btn btn-validate"
                        onClick={() => validatePayment(selectedCommand.reference)}
                        disabled={updatingStatus}
                      >
                        {updatingStatus ? (
                          <>
                            <div className="btn-spinner"></div>
                            Validation...
                          </>
                        ) : (
                          <>
                            <Check size={18} />
                            Valider le paiement
                          </>
                        )}
                      </button>
                    )}

                  {/* Close button for finished/cancelled commands */}
                  {(selectedCommand.statut === 0 ||
                    selectedCommand.statut === 5) &&
                    selectedCommand.statut_paiement != "1" &&
                    selectedCommand.statut_paiement !== null && (
                      <button
                        className="modal-action-btn btn-close"
                        onClick={handleCloseModal}
                      >
                        Fermer
                      </button>
                    )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CommandsNew;
